import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { AutosaveStatus } from './useAutosave';

export const DEFAULT_AUTOSAVE_INTERVAL_MS = 3000;
export const SAVED_STATUS_DISPLAY_MS = 1600;

export function normalizeAutosaveError(reason: unknown, fallbackMessage: string): Error {
  return reason instanceof Error
    ? reason
    : new Error((reason as { message?: string })?.message ?? fallbackMessage);
}

export function useLatestRef<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

export function useAutosaveInterval(
  enabled: boolean,
  intervalMs: number,
  callback: () => void,
): void {
  const callbackRef = useLatestRef(callback);

  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(() => callbackRef.current(), intervalMs);
    return () => window.clearInterval(timer);
  }, [callbackRef, enabled, intervalMs]);
}

export function useScopeAwareAutosaveLifecycle(scopeKey: string) {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const activeScopeRef = useRef(scopeKey);
  const savedResetTimerRef = useRef<number | null>(null);
  activeScopeRef.current = scopeKey;

  const clearSavedReset = useCallback(() => {
    if (savedResetTimerRef.current === null) return;
    window.clearTimeout(savedResetTimerRef.current);
    savedResetTimerRef.current = null;
  }, []);

  const resetFeedback = useCallback(() => {
    clearSavedReset();
    setStatus('idle');
    setError(null);
  }, [clearSavedReset]);

  useEffect(() => {
    resetFeedback();
  }, [resetFeedback, scopeKey]);

  useEffect(() => clearSavedReset, [clearSavedReset]);

  const captureScope = useCallback(() => activeScopeRef.current, []);
  const isCurrentScope = useCallback(
    (submittedScope: string) => submittedScope === activeScopeRef.current,
    [],
  );

  const beginSave = useCallback(() => {
    clearSavedReset();
    setStatus('saving');
    setError(null);
  }, [clearSavedReset]);

  const markSaved = useCallback((submittedScope: string) => {
    if (submittedScope !== activeScopeRef.current) return;
    clearSavedReset();
    setStatus('saved');
    savedResetTimerRef.current = window.setTimeout(() => {
      savedResetTimerRef.current = null;
      if (submittedScope === activeScopeRef.current) {
        setStatus((currentStatus) => currentStatus === 'saved' ? 'idle' : currentStatus);
      }
    }, SAVED_STATUS_DISPLAY_MS);
  }, [clearSavedReset]);

  const markFailed = useCallback((
    submittedScope: string,
    reason: unknown,
    fallbackMessage: string,
  ): Error => {
    const nextError = normalizeAutosaveError(reason, fallbackMessage);
    if (submittedScope === activeScopeRef.current) {
      clearSavedReset();
      setStatus('error');
      setError(nextError);
    }
    return nextError;
  }, [clearSavedReset]);

  return {
    status,
    error,
    captureScope,
    isCurrentScope,
    beginSave,
    markSaved,
    markFailed,
    resetFeedback,
  };
}
